import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { ENV } from '@environment';
import _, { isEmpty } from 'lodash';
import moment from 'moment';
import { RootState } from 'store';

import { hotelResults } from 'actions/hotels';
import Card from 'components/Cards/DataCardHotels';
import FilterTag from 'components/Filters/components/FilterTag';
import HeaderSearchDesktop from 'components/HeaderSearchDesktop';
import { Desktop, Mobile } from 'components/Responsive';
import TopButton from 'components/TopButton';
import HotelsFilters from 'containers/HotelsFilters';
import HotelsMap from 'containers/HotelsMap';
import HotelsSearchEdit from 'containers/HotelsSearchEdit';
import useIsMobile from 'hooks/useIsMobile';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { HotelFilter, HotelFilterResponse } from 'models/hotelFilter';
import { TravelHotelContentLocationRequest } from 'models/hotelLocation';
import { HotelsResultResponse, HotelsResultRequestOrderCriteriaEnum } from 'models/hotelsSearch';
import HotelDetails from 'screens/containers/HotelDetails';
import { amplitudelogEvent, SelectViewFiltersEvent } from 'services/amplitudeService';
import { HotelAmenities } from 'services/hotelService';
import { TravelArrangerUsersAllowed } from 'services/userService';
import { amplitudeEvents } from 'utils/amplitudeEvents';
import { daysCount, formatNumberToCompact, htmlT } from 'utils/generic';

import DesktopSearchBar, { SearchSwitchEnum } from '../DesktopSearchBar';
import Header from './components/Header';
import CardLoader from './components/LoadingCard';
import SmallMap from './components/SmallMap';
import {
  Container,
  Body,
  Cards,
  Filter,
  FilterIcon,
  NotFound,
  FloatingFilterContainer,
  MapIcon,
  MapLabel,
  MapButton,
  ListIcon,
  FilterTagsContainer,
  Wrapper,
  LeftContainer,
} from './styles';

interface Props {
  destinationPoint?: TravelHotelContentLocationRequest;
  checkInDate?: Date;
  checkOutDate?: Date;
  hotelsResult: HotelsResultResponse[];
  isLoading: boolean;
  isLoadingResults: boolean;
  searchId?: string;
  resultsCount?: number;
  onRequest: (orderCriteria?: HotelsResultRequestOrderCriteriaEnum) => void;
  availableFilters?: HotelFilterResponse;
  amenitiesCodes: HotelAmenities[];
  traveler?: TravelArrangerUsersAllowed;
}

const HotelsList = ({
  destinationPoint,
  checkInDate,
  checkOutDate,
  hotelsResult,
  isLoading,
  isLoadingResults,
  onRequest,
  availableFilters,
  amenitiesCodes,
  traveler,
}: Props) => {
  const isMobile = useIsMobile();
  const { t } = useStoreTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user.accountResult);
  const [showDetails, setShowDetails] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [selectedCard, setSelectedCard] = useState<number>();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [filterLoading, setFilterLoading] = useState(false);
  const { filters } = useSelector((state: RootState) => state.hotels);
  const destinationCity = useSelector(
    (state: RootState) => state.hotels.destinationPoint?.description || state.hotels.destinationPoint?.name,
  );

  const amplitudeEvent: SelectViewFiltersEvent = {
    VIEW_TYPE: '',
    TRAVEL_PRODUCT_TYPE: 'HOTELS',
    TRAVEL_FROM: '',
    TRAVEL_TO: destinationPoint?.id.toString() ?? '',
    TRAVEL_DESTINATION_TYPE: '',
    TRAVEL_TRIP_TYPE: '',
    TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), checkInDate).toString(),
    TRAVEL_DATE_FROM: checkInDate?.toString() ?? '',
    TRAVEL_DATE_TO: checkOutDate?.toString() ?? '',
    TRAVEL_TRIP_LENGTH: daysCount(checkInDate, checkOutDate).toString(),
    TRAVEL_PASSENGERS: '1',
    TRAVEL_ADULTS: '1',
    TRAVEL_CHILDREN: '0',
    TRAVEL_INFANTS: '0',
    TRAVEL_CLASS_OF_SERVICE: '',
    TRAVEL_ROOMS: '1',
    TRAVEL_WITH_BAGGAGE: '',
    TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
    SEARCHID: '',
  };

  const handleScroll = useCallback(
    (e: Event) => {
      if (e.target !== document) return;
      if (isLoading || filterLoading || isLoadingResults) return;
      const hasReachedBottom = document.body.scrollHeight - window.scrollY <= document.body.clientHeight + 700;
      if (hasReachedBottom) onRequest();
    },
    [filterLoading, isLoading, isLoadingResults, onRequest],
  );

  useEffect(() => {
    document.addEventListener('scroll', handleScroll);
    return () => document.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    history.listen((location) => {
      if (location.pathname === '/hotels') {
        setShowDetails(false);
        setShowMap(false);
        setShowFilters(false);
        setShowEdit(false);
      }
      if (location.pathname === '/hotels/details') {
        setShowDetails(true);
        setShowFilters(false);
        setShowEdit(false);
      }
      if (location.pathname === '/hotels/filters') {
        setShowDetails(false);
        setShowFilters(true);
        setShowEdit(false);
      }
      if (location.pathname === '/hotels/map') {
        setShowDetails(false);
        setShowMap(true);
        setShowFilters(false);
        setShowEdit(false);
      }
      if (location.pathname === '/hotels/edit') {
        setShowEdit(true);
      }
    });
  }, [history]);

  const handleClickCard = (hotel: HotelsResultResponse) => {
    history.push('/hotels/details');
    setSelectedCard(hotel.optionId);
    const idx = hotelsResult.findIndex((h) => h.optionId === hotel.optionId);
    setSelectedIndex(idx);
    setShowMap(false);
    setShowDetails(true);
  };

  const scrollTop = () => window.scrollTo({ top: 0 });

  const handleClickBack = () => history.goBack();

  const handleIsLoading = (isLoadingFilter: boolean) => {
    setFilterLoading(isLoadingFilter);
  };

  const handleMapClick = () => {
    if (history.location.pathname === '/hotels/map') {
      amplitudeEvent.VIEW_TYPE = 'LIST';
      history.replace('/hotels');
    } else {
      amplitudeEvent.VIEW_TYPE = 'MAP';
      history.push('/hotels/map');
    }

    amplitudelogEvent(amplitudeEvents.SELECT_VIEW_FILTERS, amplitudeEvent, user);
  };

  const handleFilterClick = () => {
    amplitudeEvent.VIEW_TYPE = 'FILTERS';
    amplitudelogEvent(amplitudeEvents.SELECT_VIEW_FILTERS, amplitudeEvent, user);
    history.push('/hotels/filters');
  };

  const handleFilterChange = async (filters: HotelFilter) => {
    setFilterLoading(true);
    setTimeout(scrollTop);
    await dispatch(hotelResults(filters));
    setFilterLoading(false);
  };

  const renderLoadingCard = () => (
    <Cards>
      {(isLoading || filterLoading) &&
        Number(3)
          .toArray()
          .map((number) => (
            <CardLoader
              key={number}
              col={{
                sm: 6,
                md: 6,
                lg: 6,
                xl: 6,
              }}
            />
          ))}
    </Cards>
  );

  const renderCards = () => {
    if (showMap && !isMobile) return null;
    if (!isLoading && !filterLoading && !isLoadingResults && hotelsResult.length === 0) {
      return (
        <NotFound>
          <NotFound.Icon />
          {t('hotelsList.notFound')}
        </NotFound>
      );
    }

    return (
      <Cards>
        {hotelsResult.map((hotel, index) => (
          <Card
            key={index}
            data={hotel}
            col={{
              sm: 6,
              md: 6,
              lg: 6,
              xl: 6,
            }}
            onClick={() => handleClickCard(hotel)}
            testId={index === 0 ? 'hotel-list-first-item' : undefined}
          />
        ))}
        {(isLoading || isLoadingResults || filterLoading) &&
          Number(3)
            .toArray()
            .map((number) => (
              <CardLoader
                key={number}
                col={{
                  sm: 6,
                  md: 6,
                  lg: 6,
                  xl: 6,
                }}
              />
            ))}
      </Cards>
    );
  };

  const renderFilterTags = () => {
    if (!filters) return null;

    return (
      <>
        {filters.categories?.map((category) => (
          <FilterTag
            key={category}
            onDelete={() => {
              const newCategories = filters.categories?.filter((c) => c !== category);
              const newFilters: HotelFilter = {
                ...filters,
                categories: newCategories,
              };
              handleFilterChange(newFilters);
            }}
          >
            {t('hotelsList.filterTags.star', { count: Number(category) })}
          </FilterTag>
        ))}

        {filters.priceFrom && filters.priceTo && (
          <FilterTag
            onDelete={() => {
              const newFilters: HotelFilter = {
                ...filters,
                priceFrom: undefined,
                priceTo: undefined,
              };
              handleFilterChange(newFilters);
            }}
          >
            {t('hotelsList.filterTags.priceRange', {
              from: formatNumberToCompact(t, filters.priceFrom),
              to: formatNumberToCompact(t, filters.priceTo),
            })}
          </FilterTag>
        )}

        {filters.zones?.map((zone) => (
          <FilterTag
            key={zone}
            onDelete={() => {
              const newZones = filters.zones?.filter((z) => z !== zone);
              const newFilters: HotelFilter = {
                ...filters,
                zones: newZones,
              };
              handleFilterChange(newFilters);
            }}
          >
            {zone}
          </FilterTag>
        ))}

        {filters.ammenities &&
          filters.ammenities
            .map((code) => amenitiesCodes.find((ac) => ac.code === code))
            .map((amenity) =>
              amenity ? (
                <FilterTag
                  key={amenity.code}
                  onDelete={() => {
                    const newAmenities = filters.ammenities?.filter((a) => a !== amenity.code);
                    const newFilters: HotelFilter = {
                      ...filters,
                      ammenities: newAmenities,
                    };
                    handleFilterChange(newFilters);
                  }}
                >
                  {amenity.name}
                </FilterTag>
              ) : null,
            )}

        {filters.refundableType && (
          <FilterTag
            onDelete={() => {
              const newFilters: HotelFilter = {
                ...filters,
                refundableType: undefined,
              };
              handleFilterChange(newFilters);
            }}
          >
            {filters.refundableType === 'Refundable'
              ? t('hotelsFilters.cancelPolicy.refundable')
              : t('hotelsFilters.cancelPolicy.nonRefundable')}
          </FilterTag>
        )}
      </>
    );
  };

  const handleEditClick = () => {
    setShowEdit(true);
    history.push('/hotels/edit');
  };

  const toggleView = (value: number) => {
    if (value === 0) {
      amplitudeEvent.VIEW_TYPE = 'LIST';
      setShowMap(false);
      history.replace('/hotels');
    } else {
      amplitudeEvent.VIEW_TYPE = 'MAP';
      setShowMap(true);
      history.push('/hotels/map');
    }

    amplitudelogEvent(amplitudeEvents.SELECT_VIEW_FILTERS, amplitudeEvent, user);
  };

  const subtitle = useMemo(() => {
    const inDate = moment(checkInDate).format('ddd[,] DD MMM');
    const outDate = moment(checkOutDate).format('ddd[,] DD MMM');
    const text = traveler ? (
      <>
        {inDate} - {outDate} {htmlT(t, 'flightsSearch.toTraveler', { user: `${traveler.name} ${traveler.lastName}` })}
      </>
    ) : (
      <>
        {inDate} - {outDate}
      </>
    );
    return text;
  }, [checkInDate, checkOutDate, t, traveler]);

  const hasFilters = useMemo(() => {
    if (isLoading) return false;
    if (filterLoading) return false;
    if (typeof filters === 'undefined' || isEmpty(filters)) return false;
    const clearFilters = Object.entries(filters).filter(([k, v]) => {
      if (k !== 'orderCriteria' && k !== 'orderDesc') {
        return _.isArray(v) ? (v.length > 0 ? true : false) : _.isString(v) ? v !== '' : !!v;
      }
      return false;
    });
    return clearFilters.length > 0;
  }, [filterLoading, filters, isLoading]);

  return (
    <>
      {!isLoading && !filterLoading && <TopButton />}
      <Desktop>
        <DesktopSearchBar activeTab={SearchSwitchEnum.Hotels} isLoading={isLoading || filterLoading} />
      </Desktop>
      <HeaderSearchDesktop
        title={t('hotelsList.title', { city: destinationCity || '' })}
        subtitle={subtitle}
        onChange={toggleView}
        initial={showMap ? 1 : 0}
        hasFilters={hasFilters}
        isLoading={isLoading || filterLoading}
        isEmpty={hotelsResult.length === 0}
      />
      {!isLoading && !filterLoading && (
        <Desktop>
          <FilterTagsContainer>{renderFilterTags()}</FilterTagsContainer>
        </Desktop>
      )}
      <Container>
        <Mobile>
          <Header
            info={{
              destinationPoint,
              checkInDate,
              checkOutDate,
            }}
            loading={isLoading || filterLoading}
            goBack={handleClickBack}
            onEdit={handleEditClick}
          />
        </Mobile>

        {!showMap && (
          <LeftContainer>
            <Desktop>
              <SmallMap
                results={hotelsResult}
                text={t('hotelsList.showMap')}
                onClick={() => toggleView(1)}
                isLoading={isLoading || filterLoading}
              />
            </Desktop>
            {(isLoading || filterLoading || (hotelsResult.length === 0 && hasFilters) || hotelsResult.length > 0) && (
              <HotelsFilters
                show={showFilters}
                onClose={handleClickBack}
                onChange={handleFilterChange}
                availableFilters={availableFilters}
                amenitiesCodes={amenitiesCodes}
                destinationPoint={destinationPoint}
                checkInDate={checkInDate}
                checkOutDate={checkOutDate}
                isLoading={isLoading || filterLoading}
              />
            )}
          </LeftContainer>
        )}

        {isLoading || filterLoading ? (
          <Wrapper>{renderLoadingCard()}</Wrapper>
        ) : (
          <>
            <Wrapper>
              <Mobile>
                <FilterTagsContainer>{renderFilterTags()}</FilterTagsContainer>
              </Mobile>

              <Body>
                {!isLoading && !filterLoading && !showFilters && !showDetails && (isMobile || showMap) && (
                  <FloatingFilterContainer>
                    <Filter>
                      <div onClick={handleMapClick} data-testid="hotel-list-filter-map">
                        <MapButton isVisible={!showMap}>
                          <MapIcon />
                          <MapLabel>{t('hotelsList.viewMap')}</MapLabel>
                        </MapButton>
                        <MapButton isVisible={showMap}>
                          <ListIcon />
                          <MapLabel>{t('hotelsList.viewList')}</MapLabel>
                        </MapButton>
                      </div>
                      {!showMap && (
                        <>
                          <Filter.Divider />
                          <div onClick={handleFilterClick} data-testid="hotel-list-filter-button">
                            <FilterIcon />
                            <Filter.Label>{t('flightsList.filter')}</Filter.Label>
                          </div>
                        </>
                      )}
                    </Filter>
                  </FloatingFilterContainer>
                )}
                {renderCards()}
              </Body>
            </Wrapper>
            <HotelsMap
              show={showMap}
              hotelsResult={hotelsResult}
              destinationPoint={destinationPoint}
              onClick={handleClickCard}
              onClose={handleClickBack}
            />
          </>
        )}
      </Container>

      <HotelDetails
        show={showDetails}
        optionId={selectedCard}
        onClose={handleClickBack}
        amenitiesCodes={amenitiesCodes}
        traveler={traveler}
        index={selectedIndex}
        destinationPoint={destinationPoint}
        checkInDate={checkInDate}
        checkOutDate={checkOutDate}
        onFilterChange={handleFilterChange}
        isFilterLoading={handleIsLoading}
      />

      <Mobile>
        <HotelsSearchEdit show={showEdit} onClose={handleClickBack} />
      </Mobile>
    </>
  );
};

export default HotelsList;
